import { Box, Button, FormControlLabel, Radio, Text, TextField } from '@streamelements/frontend-ui';
import { createFileRoute, useRouter } from '@tanstack/react-router';
import * as Combobox from '../../../components/Combobox';
import { PlusIcon } from '../../../components/Icons';
import {
  AllowedProvider,
  allowedConnectOptionsSchema,
  platformPrettyName,
  providersOrder,
} from '../../../constants/common';
import { providerIcons } from '../../../components/ProviderIcon';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

export const Route = createFileRoute('/_authenticated/onboarding/setup-multistreaming/_layout/')({
  component: SetupMultiStreamingAccountPage,
});

const formSchema = z.object({
  provider: allowedConnectOptionsSchema,
  orientation: z.enum(['vertical', 'horizontal']).optional(),
});

type FormValues = z.infer<typeof formSchema>;

function SetupMultiStreamingAccountPage() {
  const router = useRouter();
  const form = useForm<FormValues>({
    defaultValues: {
      provider: '' as never,
      orientation: 'horizontal',
    },
    resolver: zodResolver(formSchema),
  });

  const onSubmit = (data: FormValues) => {
    const { provider, orientation } = data;

    if (!provider) {
      return;
    }

    const createVerticalComposition = (() => {
      if (provider === 'tiktok') {
        return orientation === 'vertical';
      }

      if (provider === 'youtube') {
        return true;
      }

      return false;
    })();

    router.navigate({
      to: '/onboarding/setup-multistreaming/connect',
      search: {
        provider,
        createVerticalComposition,
      },
    });
  };

  const selectedProvider = form.watch('provider');
  const SelectTriggerIcon = selectedProvider ? providerIcons[selectedProvider] : PlusIcon;

  return (
    <Box
      css={{
        display: 'flex',
        flexDirection: 'column',
        gap: '1rem',
        height: '100%',
      }}
      as="form"
      onSubmit={form.handleSubmit(onSubmit)}
    >
      <Box css={{ px: '1rem' }}>
        <Text.Subtitle weight="bold" css={{ m: 0 }}>
          Connect your 2nd channel
        </Text.Subtitle>
        <Text.Body variant="caption" css={{ color: '$textPrimary50' }}>
          Connect your channels you want to multi-stream to as your 2nd channel
        </Text.Body>
      </Box>

      <Box css={{ px: '0.5rem', flex: 1 }}>
        <Combobox.ComboBoxRoot
          defaultInputValue="Select a platform"
          value={[selectedProvider]}
          name="provider"
          onValueChange={([next]) =>
            form.setValue('provider', next as AllowedProvider, {
              shouldValidate: true,
              shouldDirty: true,
            })
          }
        >
          <Combobox.ComboBoxTrigger inputProps={{ readOnly: true, 'aria-label': 'Connect a platform' }}>
            <TextField.Adornment
              position="start"
              css={{
                '&[data-selected-provider=""]': { color: '$textPrimary50' },
              }}
              data-selected-provider={selectedProvider}
            >
              <SelectTriggerIcon />
            </TextField.Adornment>
          </Combobox.ComboBoxTrigger>
          <Combobox.ComboBoxContent>
            {providersOrder.map((provider) => {
              if (provider === 'custom') {
                return null;
              }

              const Icon = providerIcons[provider];
              const value = platformPrettyName[provider];

              return (
                <Combobox.ComboBoxOption
                  key={provider}
                  value={provider}
                  label={value}
                  css={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                >
                  <Icon />
                  {value}
                </Combobox.ComboBoxOption>
              );
            })}
          </Combobox.ComboBoxContent>
        </Combobox.ComboBoxRoot>

        {selectedProvider === 'tiktok' && (
          <>
            <Text.Body css={{ mt: '1rem', mb: '0.5rem' }} weight="bold">
              How would you like to stream to Tiktok?
            </Text.Body>

            <Controller
              control={form.control}
              name="orientation"
              render={({ field }) => {
                return (
                  <Box
                    as={Radio.Root}
                    css={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '0.5rem',
                    }}
                    value={field.value}
                    onValueChange={(next) => {
                      field.onChange({
                        target: { name: field.name, value: next },
                      });
                    }}
                    name={field.name}
                  >
                    <FormControlLabel>
                      <Radio.Item value="vertical" />
                      Vertical
                    </FormControlLabel>
                    <FormControlLabel>
                      <Radio.Item value="horizontal" />
                      Horizontal
                    </FormControlLabel>
                  </Box>
                );
              }}
            />
          </>
        )}
      </Box>

      <Box css={{ padding: '0.5rem', bgc: '$uiQuaternary' }}>
        <Button color="neutral" density="compact" css={{ width: '100%' }} disabled={!selectedProvider} type="submit">
          Connect with {platformPrettyName[selectedProvider] ?? '...'}
        </Button>
      </Box>
    </Box>
  );
}
